import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Autocomplete,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  Close,
  Delete,
  Add,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SoldProduct, Product, Client, PaymentMethod } from '../types/entities';
import { SoldProductFormData, PaymentFormData } from '../types/forms';

interface SelectedProduct {
  product: Product;
  amount: number;
  price: number;
}

interface SellProductDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: SoldProductFormData, selectedProducts: SelectedProduct[]) => Promise<void>;
  editingSale?: SoldProduct | null;
  products: Product[];
  clients: Client[];
  onCreateProduct?: (initialName?: string) => void;
  onCreateClient?: () => void;
  newlyCreatedProduct?: Product | null;
}

const SellProductDialog: React.FC<SellProductDialogProps> = ({
  open,
  onClose,
  onSubmit,
  editingSale,
  products,
  clients,
  onCreateProduct,
  onCreateClient,
  newlyCreatedProduct,
}) => {
  const { t } = useTranslation();

  // Form state
  const [formData, setFormData] = useState<SoldProductFormData>({
    product_id: 0,
    amount: 1,
    price: 0,
    client_id: 0,
  });

  // Multi-product selection state
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);

  // Product search input state
  const [productSearchInput, setProductSearchInput] = useState<string>('');

  // Payment state
  const [payments, setPayments] = useState<PaymentFormData[]>([
    { method: PaymentMethod.CASH, amount: 0 }
  ]);

  // Validation state
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof SoldProductFormData, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof SoldProductFormData, boolean>>>({});

  // Initialize form when dialog opens or editingSale changes
  useEffect(() => {
    if (open) {
      if (editingSale) {
        setFormData({
          product_id: editingSale.product.id,
          amount: editingSale.amount,
          price: editingSale.price,
          client_id: editingSale.client.id,
          discount_id: editingSale.discount?.id,
        });
        setSelectedProducts([]);
        // Initialize payments from existing sale
        if (editingSale.payments && editingSale.payments.length > 0) {
          setPayments(editingSale.payments.map(p => ({
            method: p.method as 'cash' | 'card' | 'voucher',
            amount: parseFloat(p.amount.toString())
          })));
        } else {
          setPayments([{ method: PaymentMethod.CASH, amount: editingSale.price }]);
        }
      } else {
        setFormData({
          product_id: 0,
          amount: 1,
          price: 0,
          client_id: 0,
        });
        setSelectedProducts([]);
        setPayments([{ method: PaymentMethod.CASH, amount: 0 }]);
      }
      setFormErrors({});
      setTouched({});
      setProductSearchInput('');
    }
  }, [open, editingSale]);

  // Auto-add newly created product to selected products
  useEffect(() => {
    if (newlyCreatedProduct && !editingSale && open) {
      setSelectedProducts(prev => {
        const existingIndex = prev.findIndex(p => p.product.id === newlyCreatedProduct.id);
        if (existingIndex >= 0) {
          // Update existing product - increment amount
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            amount: updated[existingIndex].amount + 1
          };
          return updated;
        } else {
          // Add new product
          const newProduct = {
            product: newlyCreatedProduct,
            amount: 1,
            price: newlyCreatedProduct.sell_price ? parseFloat(newlyCreatedProduct.sell_price) : 0
          };
          // Update payment amount to match total if it's the first product
          if (prev.length === 0) {
            setPayments([{ method: PaymentMethod.CASH, amount: newProduct.price }]);
          }
          return [...prev, newProduct];
        }
      });
      setProductSearchInput('');
    }
  }, [newlyCreatedProduct, open, editingSale]);

  // Update payment amount when total changes (for new sales)
  useEffect(() => {
    if (!editingSale && selectedProducts.length > 0) {
      const total = selectedProducts.reduce((sum, item) => sum + (item.amount * item.price), 0);
      if (payments.length === 1 && payments[0].amount === 0) {
        setPayments([{ method: PaymentMethod.CASH, amount: total }]);
      }
    }
  }, [selectedProducts, editingSale]);

  // Validation functions
  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof SoldProductFormData, string>> = {};
    
    if (!formData.client_id || formData.client_id === 0) {
      errors.client_id = t('validation.required');
    }
    
    if (selectedProducts.length === 0 && !editingSale) {
      // Only validate products if creating a new sale
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldBlur = (field: keyof SoldProductFormData) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateForm();
  };

  // Multi-product functions
  const addProductToSelection = (product: Product) => {
    const existingIndex = selectedProducts.findIndex(p => p.product.id === product.id);
    if (existingIndex >= 0) {
      // Update existing product
      const updated = [...selectedProducts];
      updated[existingIndex] = {
        ...updated[existingIndex],
        amount: updated[existingIndex].amount + 1
      };
      setSelectedProducts(updated);
    } else {
      // Add new product
      setSelectedProducts([...selectedProducts, {
        product,
        amount: 1,
        price: product.sell_price ? parseFloat(product.sell_price) : 0
      }]);
    }
  };

  const removeProductFromSelection = (productId: number) => {
    setSelectedProducts(selectedProducts.filter(p => p.product.id !== productId));
  };

  const updateProductAmount = (productId: number, amount: number) => {
    if (amount <= 0) {
      removeProductFromSelection(productId);
      return;
    }
    
    setSelectedProducts(selectedProducts.map(p => 
      p.product.id === productId ? { ...p, amount } : p
    ));
  };

  const updateProductPrice = (productId: number, price: number) => {
    setSelectedProducts(selectedProducts.map(p => 
      p.product.id === productId ? { ...p, price } : p
    ));
  };

  const calculateTotalPrice = () => {
    return selectedProducts.reduce((total, item) => total + (item.amount * item.price), 0);
  };

  const calculateTotalPaid = () => {
    return payments.reduce((sum, p) => sum + p.amount, 0);
  };

  const getTotalPrice = () => {
    if (editingSale) {
      return formData.price * formData.amount;
    }
    return calculateTotalPrice();
  };

  const arePaymentsBalanced = (): boolean => {
    const totalPrice = getTotalPrice();
    const totalPaid = calculateTotalPaid();
    // Allow small floating point differences (0.01)
    return Math.abs(totalPrice - totalPaid) < 0.01;
  };

  const isFormValid = (): boolean => {
    const hasClient = formData.client_id > 0;
    const hasProducts = editingSale ? true : selectedProducts.length > 0;
    const paymentsBalanced = arePaymentsBalanced();
    
    return hasClient && hasProducts && paymentsBalanced;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
    }).format(amount);
  };

  const handleSaveSale = async () => {
    if (!validateForm() || !isFormValid()) {
      return;
    }

    try {
      const formDataWithPayments = { ...formData, payments };
      await onSubmit(formDataWithPayments, selectedProducts);
      // Don't close here - let parent handle it
    } catch (err) {
      // Error is handled by parent
    }
  };

  // Filtered products to avoid selecting duplicates (but include all products, even with amount 0)
  const availableProducts = products.filter(p => !selectedProducts.some(sp => sp.product.id === p.id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {editingSale ? t('sales.editSale') : t('sales.addNewSale')}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ ml: 2 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Client Selection */}
          <Autocomplete
            fullWidth
            options={clients}
            getOptionLabel={(option) => option.name}
            value={clients.find(c => c.id === formData.client_id) || null}
            onChange={(event, newValue) => {
              setFormData({ ...formData, client_id: newValue?.id || 0 });
            }}
            onBlur={handleFieldBlur('client_id')}
            noOptionsText={
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('clients.noClientsFound')}
                </Typography>
                {onCreateClient && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onCreateClient}
                  >
                    {t('clients.addClient')}
                  </Button>
                )}
              </Box>
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('sales.client')}
                required
                placeholder={t('sales.searchPlaceholder')}
                error={touched.client_id && !!formErrors.client_id}
                helperText={touched.client_id && formErrors.client_id}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.phone_number}
                  </Typography>
                </Box>
              </Box>
            )}
          />

          {!editingSale && (
            <>
              {/* Product Search and Add */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                <Autocomplete
                  fullWidth
                  options={availableProducts}
                  getOptionLabel={(option) => option.name}
                  getOptionDisabled={(option) => option.amount === 0}
                  value={null}
                  inputValue={productSearchInput}
                  onInputChange={(event, newInputValue) => {
                    setProductSearchInput(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      addProductToSelection(newValue);
                      setProductSearchInput('');
                    }
                  }}
                  noOptionsText={
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('products.noProductsFound')}
                      </Typography>
                      {onCreateProduct && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => onCreateProduct(productSearchInput.trim())}
                        >
                          {t('products.addProduct')}
                        </Button>
                      )}
                    </Box>
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('sales.searchAndAddProduct')}
                      placeholder={t('sales.searchPlaceholder')}
                    />
                  )}
                  renderOption={(props, option) => {
                    const isDisabled = option.amount === 0;
                    return (
                      <Box component="li" {...props} sx={{ opacity: isDisabled ? 0.5 : 1 }}>
                        <Box>
                          <Typography 
                            variant="body1"
                            sx={{ 
                              color: isDisabled ? 'text.disabled' : 'text.primary',
                              fontWeight: isDisabled ? 'normal' : 'inherit'
                            }}
                          >
                            {option.name}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: isDisabled ? 'text.disabled' : 'text.secondary' 
                            }}
                          >
                            {t('sales.available')}: {option.amount} | {t('sales.price')}: {option.sell_price ? `${option.sell_price} zł` : t('sales.noPrice')}
                            {isDisabled && ` (${t('sales.outOfStock') || 'Out of stock'})`}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                />
              </Box>

              {/* Selected Products */}
              {selectedProducts.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {t('sales.selectedProducts')} ({selectedProducts.length})
                  </Typography>
                  
                  {selectedProducts.map((productItem, index) => (
                    <Box key={productItem.product.id} sx={{ 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 2, 
                      p: 2, 
                      mb: 2,
                      position: 'relative'
                    }}>
                      <IconButton
                        size="small"
                        onClick={() => removeProductFromSelection(productItem.product.id)}
                        sx={{ 
                          position: 'absolute', 
                          top: 8, 
                          right: 8,
                          color: 'error.main'
                        }}
                      >
                        <Delete />
                      </IconButton>
                      
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {t('sales.product')} {index + 1}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {productItem.product.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('sales.available')}: {productItem.product.amount}
                          </Typography>
                        </Box>
                        
                        <TextField
                          label={t('sales.quantity')}
                          type="number"
                          value={productItem.amount}
                          onChange={(e) => updateProductAmount(productItem.product.id, parseInt(e.target.value) || 1)}
                          inputProps={{ min: 1, max: productItem.product.amount }}
                          sx={{ width: 120 }}
                        />
                        
                        <TextField
                          label={t('sales.price')}
                          type="number"
                          value={productItem.price}
                          onChange={(e) => updateProductPrice(productItem.product.id, parseFloat(e.target.value) || 0)}
                          inputProps={{ step: 0.01 }}
                          sx={{ width: 120 }}
                        />
                        
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          width: 140,
                          position: 'relative'
                        }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              transform: 'translate(0px, -9px) scale(0.75)',
                              transformOrigin: 'top left',
                              color: 'rgba(0, 0, 0, 0.6)',
                              fontSize: '0.75rem',
                              fontWeight: 400,
                              lineHeight: 1.4375,
                              letterSpacing: '0.00938em',
                              pointerEvents: 'none',
                              userSelect: 'none'
                            }}
                          >
                            {t('sales.price')}
                          </Typography>
                          <Box sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            height: '56px',
                            paddingLeft: 0,
                            paddingRight: '14px',
                            boxSizing: 'border-box',
                            textAlign: 'left'
                          }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: 'primary.main',
                                fontSize: '1rem',
                                lineHeight: 1.5,
                                margin: 0,
                                padding: 0,
                                textAlign: 'left',
                                width: '100%'
                              }}
                            >
                              {formatCurrency(productItem.amount * productItem.price)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                  
                </Box>
              )}
            </>
          )}

          {editingSale && (() => {
            // Calculate max amount: current product amount + current sale amount
            // This is the maximum allowed (user can increase up to this, but not beyond)
            const maxAllowedAmount = editingSale.product.amount + editingSale.amount;
            const currentAvailable = editingSale.product.amount;
            
            // Calculate available amount after edit: current + original sale - new amount
            // If increasing: available decreases by the difference
            // If decreasing: available increases by the difference
            const amountDifference = formData.amount - editingSale.amount;
            const availableAfterEdit = currentAvailable - amountDifference;
            
            return (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 2, 
                  p: 2
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('sales.product')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {editingSale.product.name}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color={availableAfterEdit >= 0 ? 'text.secondary' : 'error.main'}
                    sx={{ fontWeight: availableAfterEdit < 0 ? 'bold' : 'normal' }}
                  >
                    {t('sales.available')}: {formData.amount !== editingSale.amount ? Math.max(0, availableAfterEdit) : currentAvailable}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label={t('sales.quantity')}
                    type="number"
                    value={formData.amount}
                    onChange={(e) => {
                      const newAmount = parseInt(e.target.value) || 1;
                      // Limit to max allowed amount
                      const limitedAmount = Math.min(newAmount, maxAllowedAmount);
                      setFormData({ ...formData, amount: limitedAmount });
                    }}
                    inputProps={{ 
                      min: 1,
                      max: maxAllowedAmount
                    }}
                    sx={{ flex: 1 }}
                    helperText={formData.amount > editingSale.amount && currentAvailable < (formData.amount - editingSale.amount) 
                      ? t('sales.insufficientStockMessage', { 
                          available: currentAvailable, 
                          requested: formData.amount - editingSale.amount 
                        }) 
                      : undefined}
                    error={formData.amount > maxAllowedAmount || availableAfterEdit < 0}
                  />                 
                </Box>
              </Box>
            );
          })()}

          {/* Payment Section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('sales.paymentDetails') || t('appointments.paymentDetails')}
            </Typography>
            
            {payments.map((payment, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2, my: 2, alignItems: 'center' }}>
                <FormControl fullWidth sx={{ minWidth: 150 }}>
                  <InputLabel>{t('appointments.paymentMethod')}</InputLabel>
                  <Select
                    value={payment.method}
                    label={t('appointments.paymentMethod')}
                    onChange={(e) => {
                      const newPayments = [...payments];
                      newPayments[index].method = e.target.value as 'cash' | 'card' | 'voucher';
                      setPayments(newPayments);
                    }}
                  >
                    <MenuItem value={PaymentMethod.CASH}>{t('appointments.cash')}</MenuItem>
                    <MenuItem value={PaymentMethod.CARD}>{t('appointments.card')}</MenuItem>
                    <MenuItem value={PaymentMethod.VOUCHER}>{t('appointments.voucher')}</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={t('appointments.amount') + ' (zł)'}
                  type="number"
                  value={payment.amount}
                  onChange={(e) => {
                    const newPayments = [...payments];
                    newPayments[index].amount = parseFloat(e.target.value) || 0;
                    setPayments(newPayments);
                  }}
                  fullWidth
                  inputProps={{ step: "0.01", min: "0" }}
                />
                {payments.length > 1 && (
                  <IconButton 
                    onClick={() => setPayments(payments.filter((_, i) => i !== index))}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                )}
              </Box>
            ))}

            <Button
              onClick={() => setPayments([...payments, { method: PaymentMethod.CASH, amount: 0 }])}
              variant="outlined"
              size="small"
              startIcon={<Add />}
              sx={{ mt: 1 }}
            >
              {t('appointments.addAnotherPayment')}
            </Button>

            {(!editingSale ? selectedProducts.length > 0 : true) && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('sales.totalPrice')}: {formatCurrency(getTotalPrice())}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('sales.totalPaid') || 'Total Paid'}: {formatCurrency(calculateTotalPaid())}
                  </Typography>
                </Box>
                
                {!arePaymentsBalanced() && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {t('sales.paymentMismatchWarning') || 'Total paid must equal total price'}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleSaveSale} 
          variant="contained"
          disabled={!isFormValid()}
        >
          {editingSale ? t('common.update') : t('common.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SellProductDialog;

